/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { BuilderPermission } from '@gh/builder-shared-data';
import {
  newDirtyRow,
  PlainImmutablePageState,
  removeDirtyRow,
  setEntityFields,
  Table,
  UserContext,
  whenExisting,
  whenModified,
} from '@gh/common-shared';
import { FormMode } from '@gh/core-ui';
import { switchMap$ } from '@gh/rx/operators';
import { pick, uniq } from 'lodash';
import {
  CALENDAR_DETAILS_INIT,
  CalendarDetails,
  CalendarListItem,
  calendarTable,
  createNewCalendarListItem,
  generateDateId,
  getCalendarListItemId,
} from '../../resources/scheduling-calendar-details.model';

@Injectable()
export class SchedulingCalendarDetailsFormState extends PlainImmutablePageState<CalendarDetails> {
  selectedDate$ = this.select(({ selectedDate }) => selectedDate);
  visibleYear$ = this.select(({ visibleDate }) => visibleDate.getFullYear());
  datesOfVisibleYear$ = this.visibleYear$.pipe(
    switchMap$((year) => this.select(({ dates }) => calendarTable.byGroup(dates, 'year', year))));
  currentDescription$ = this.select(({ currentDescription }) => currentDescription);

  constructor(private userContext: UserContext) {
    super(CALENDAR_DETAILS_INIT, FormMode.EditMode);
  }

  get visibleYear(): number {
    return this.value.visibleDate.getFullYear();
  }

  get dirtyRecords(): CalendarListItem[] {
    return this.project(({ dates }) => calendarTable.all(dates).filter(whenModified));
  }

  get isSelectedDateExist(): boolean {
    const record = this.project(({ dates, selectedDate }) => calendarTable.byId(dates, generateDateId(selectedDate)));
    return record ? whenExisting(record) : false;
  }

  build(year: number, calendar: CalendarListItem[]): void {
    if (!this.userContext.hasPermission(BuilderPermission.Setup.Schedules.Calendar.EDIT)) {
      this.setMode(FormMode.ViewMode);
    }

    this.setValue({
      ...CALENDAR_DETAILS_INIT,
      selectedDate: this.value.selectedDate,
      visibleDate: this.value.visibleDate,
    });
    this.loadRecords(year, calendar);
    this.resetDirty();
  }

  isYearPreloaded(year: number): boolean {
    return this.value.preloadedYears.includes(year);
  }

  addRecord(): void {
    const { selectedDate, currentDescription } = this.value;
    this.startChangeDetection();
    this.setField('currentDescription', '');
    this.updateDates((records) => calendarTable.merge(
      records,
      createNewCalendarListItem(selectedDate, currentDescription),
      // set ID of old row it if exists
      (oldRow, newRow) => newDirtyRow(
        oldRow,
        oldRow ? setEntityFields(newRow, pick(oldRow, 'id', 'version')) : newRow)));
    this.setDirtyIfChanged();
  }

  deleteRecord(record: CalendarListItem): void {
    this.startChangeDetection();
    this.updateDates((records) => calendarTable.mergeById(records, getCalendarListItemId(record), removeDirtyRow));
    this.setDirtyIfChanged();
  }

  setSelectedDate(date: Date): void {
    this.setField('selectedDate', date);
  }

  setVisibleDate(date: Date): void {
    this.setField('visibleDate', date);
  }

  setCurrentDescription(description: string): void {
    this.setField('currentDescription', description);
  }

  loadRecords(year: number, newRecords: CalendarListItem[]): void {
    this.updateDates((records) => calendarTable.insertAll(records, newRecords));
    this.setField('preloadedYears', uniq(this.value.preloadedYears.concat([year])));
  }

  private updateDates(calendarTransform: IdentityFn<Table<CalendarListItem>>): void {
    this.setField('dates', calendarTransform(this.value.dates));
  }
}
